// Lazy Loading Handler
// - Lazy-load für Elemente mit `data-src`
// - Verwendet IntersectionObserver für Performance
// - Video-Setup für proxy loading via HTML

type LoadableElement = HTMLImageElement | HTMLIFrameElement | HTMLElement;

type VideoSources = {
	desktop: Record<string, string>;
	mobile: Record<string, string>;
};

// Speichern der gehashten Video-Pfade pro Video (Map für mehrere Videos)
const videoSourcesMap = new Map<HTMLVideoElement, VideoSources>();

function isImage(el: Element): el is HTMLImageElement {
	return el.tagName.toLowerCase() === 'img';
}

function loadResource(el: LoadableElement): Promise<void> {
	return new Promise((resolve) => {
		if (isImage(el)) {
			const img = el as HTMLImageElement;
			const src = img.dataset.src;
			if (!src) return resolve();
			img.onload = () => img.classList.add('loaded');
			img.onerror = () => img.classList.add('error');
			img.src = src;
			delete img.dataset.src;
			return resolve();
		}

		const src = el.getAttribute('data-src');
		if (src) {
			if (el instanceof HTMLIFrameElement) {
				el.src = src;
			} else {
				el.setAttribute('style', (el.getAttribute('style') || '') + `background-image:url('${src}')`);
			}
			el.removeAttribute('data-src');
		}
		resolve();
	});
}

/**
 * Capture gehashte Video-Pfade aus HTML und src-Attribute entfernen
 * Wird bei DOMContentLoaded aufgerufen
 * Funktioniert mit allen video[data-lazy-video] Elementen
 */
export function captureVideoSources(): void {
	const videos = document.querySelectorAll('video[data-lazy-video]') as NodeListOf<HTMLVideoElement>;
	
	if (!videos.length) return;

	videos.forEach((video) => {
		const sources: VideoSources = {
			desktop: {},
			mobile: {}
		};

		Array.from(video.querySelectorAll('source')).forEach((source) => {
			const src = source.getAttribute('src');
			const type = source.getAttribute('type') || '';
			if (!src) return;

			const isMobile = source.getAttribute('data-mobile') === 'true';
			if (isMobile) {
				sources.mobile[type] = src;
			} else {
				sources.desktop[type] = src;
			}
			
			// src entfernen, damit Video nicht sofort geladen wird
			source.removeAttribute('src');
		});

		videoSourcesMap.set(video, sources);
	});
}

/**
 * Setup alle Lazy-Videos mit der richtigen Variante (mobile/desktop)
 * Wird bei window load aufgerufen
 */
export function setupLazyVideos(): void {
	const isMobile = window.matchMedia('(max-width:768px)').matches;

	videoSourcesMap.forEach((sources, video) => {
		const variant = isMobile ? sources.mobile : sources.desktop;

		Array.from(video.querySelectorAll('source')).forEach((source) => {
			const type = source.getAttribute('type') || '';
			if (variant[type]) {
				source.setAttribute('src', variant[type]);
			}
		});

		video.load();
		video.autoplay = true;
		video.play().catch(() => {});
	});
}

export function lazyLoadImages(root: ParentNode = document): Promise<void> {
	return new Promise((resolve) => {
		const nodes = Array.from(root.querySelectorAll('[data-src]')) as Element[];
		if (!nodes.length) {
			resolve();
			return;
		}

		if ('IntersectionObserver' in window) {
			const io = new IntersectionObserver((entries, obs) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) return;
					const el = entry.target as LoadableElement;
					loadResource(el).catch(() => {});
					obs.unobserve(entry.target);
				});
			}, { root: null, threshold: 0 });

			nodes.forEach(n => io.observe(n));
			resolve(); // IntersectionObserver Setup fertig
		} else {
			// Fallback: load all immediately
			Promise.all(nodes.map(n => loadResource(n as LoadableElement)))
				.then(() => resolve())
				.catch(() => resolve());
		}
	});
}
